import { runInAction } from 'mobx';
import { Button, Grid, GridColumn, Heading, Modal, ModalContent, Notice, PassField, Separator, Text, TextField } from '@teliaee/design-system';
import { observer } from 'mobx-react';
import React from 'react';
import useStores from '../stores/UseStores';
import { waitForModalAnimation } from '../util/AnimationUtil';
import { EMAIL_FIELD_ID, PW_FIELD_ID, emailFieldHelper, getHelperErrorMessage, helperInErrorState, notEmptyFieldHelper } from '../util/FieldHelperUtil';
import LoginButton from '../components/LoginButton';
import { runInTeliaRumService } from '../util/TeliaOtelUtil';
import { isEmailValid } from '../util/ValidationUtil';
import UiNotice from '../components/UiNotice';
import { useDidUpdate } from '../util/hooks/useDidUpdate';

const MergeLoginModal = observer((): React.JSX.Element => {
  const {
    mergeStore,
    emailLoginStore,
    uiStore: { showErrorNoticeWithErroredFields },
    addEmailStore,
    forgotPasswordStore,
    translateStore: { translate },
  } = useStores();
  const { emailLoginCredentials, submitting } = emailLoginStore;
  const [hasSubmitted, setHasSubmitted] = React.useState<boolean>(false);

  const getEmailHelper = (forceIsDirty?: boolean) => emailFieldHelper(translate, emailLoginCredentials.emailAddress, forceIsDirty ?? hasSubmitted);
  const getPasswordHelper = (forceIsDirty?: boolean) =>
    notEmptyFieldHelper(translate, emailLoginCredentials.password, forceIsDirty ?? hasSubmitted, PW_FIELD_ID);

  useDidUpdate(() => {
    if (mergeStore.mergeLoginModalOpen) {
      runInAction(() => {
        emailLoginCredentials.password = '';
      });
      setHasSubmitted(false);
    }
  }, [mergeStore.mergeLoginModalOpen]);

  const isAnyCredentialInvalid = () => {
    return !emailLoginCredentials.emailAddress || !emailLoginCredentials.password || !isEmailValid(emailLoginCredentials.emailAddress);
  };

  const switchToPasswordForget = React.useCallback(() => {
    if (addEmailStore.email) {
      forgotPasswordStore.email = addEmailStore.email;
    }
    mergeStore.setMergeLoginModalState(false);
    waitForModalAnimation(() => forgotPasswordStore.setForgotPasswordModalState(true));
  }, []);

  const handleFormSubmit = (event: React.FormEvent): void => {
    runInTeliaRumService('Autentimine (email)', () => {
      setHasSubmitted(true);
      event.preventDefault();
      if (!isAnyCredentialInvalid()) {
        emailLoginStore.handleLogin();
      } else {
        showErrorNoticeWithErroredFields('form.error', [getEmailHelper(true), getPasswordHelper(true)]);
      }
    });
  };

  return (
    <Modal
      isOpen={mergeStore.mergeLoginModalOpen}
      size="6-col"
      aria-labelledby="email-exists-modal-title"
      onClose={() => mergeStore.setMergeLoginModalState(false)}
    >
      <ModalContent>
        <UiNotice only={['form.error', 'email.mailServiceError']} />
        <form onSubmit={handleFormSubmit} id="emailLogin">
          <Grid align={['center-xs']} className="text-center">
            <GridColumn width={['xs-10', 'sm-8', 'md-8']}>
              <Text>
                <Notice direction="vertical">
                  <Heading level={1} className="h3 text-center" id="email-exists-modal-title">
                    {translate('MergeLoginModal.title')}
                  </Heading>
                </Notice>
                <Separator spacing="xsmall" color="invisible" />
                <p>{translate('MergeLoginModal.p', { email: emailLoginCredentials.emailAddress })}</p>
                <TextField
                  label={translate('panel.email.input')}
                  id={EMAIL_FIELD_ID}
                  value={emailLoginCredentials.emailAddress}
                  isInvalid={helperInErrorState(getEmailHelper())}
                  isReadOnly={true}
                />
                <PassField
                  label={translate('panel.email.password')}
                  id={PW_FIELD_ID}
                  value={emailLoginCredentials.password}
                  onChange={(value) => (emailLoginCredentials.password = value)}
                  isInvalid={helperInErrorState(getPasswordHelper())}
                  errorMessage={getHelperErrorMessage(getPasswordHelper())}
                  required={true}
                  input={{ autoComplete: 'current-password' }}
                />
                <p>
                  <LoginButton isLoading={submitting} disabled={submitting} />
                </p>
                <p>
                  <Button onClick={switchToPasswordForget} text={translate('panel.email.forgotPassword')} link={true} />
                </p>
              </Text>
            </GridColumn>
          </Grid>
        </form>
      </ModalContent>
    </Modal>
  );
});

export default MergeLoginModal;
